import React from 'react';
import Button from '../ui/Button';

const EmptyState = ({
  icon,
  title = 'No data found',
  description = '',
  actionLabel,
  onAction,
  className = '',
  size = 'md'
}) => {
  const getDefaultIcon = () => (
    <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={1} 
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
      />
    </svg>
  );

  const sizes = {
    sm: {
      container: 'p-8',
      icon: 'w-12 h-12',
      title: 'text-lg',
      description: 'text-sm',
      spacing: 'space-y-3'
    },
    md: {
      container: 'p-12',
      icon: 'w-16 h-16',
      title: 'text-xl',
      description: 'text-base',
      spacing: 'space-y-4'
    },
    lg: {
      container: 'p-16',
      icon: 'w-20 h-20',
      title: 'text-2xl',
      description: 'text-lg',
      spacing: 'space-y-6'
    }
  };

  const sizeConfig = sizes[size];

  return (
    <div className={`
      flex flex-col items-center justify-center text-center
      ${sizeConfig.container} ${sizeConfig.spacing} ${className}
    `}>
      {/* Icon */}
      <div className={`${sizeConfig.icon} text-text-quaternary mb-2`}>
        {icon || getDefaultIcon()}
      </div>

      {/* Title */}
      <h3 className={`font-semibold text-text-primary ${sizeConfig.title}`}>
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className={`text-text-secondary max-w-md ${sizeConfig.description}`}>
          {description}
        </p>
      )}

      {/* Action Button */}
      {actionLabel && onAction && (
        <div className="mt-2">
          <Button 
            variant="outline" 
            onClick={onAction}
            className="mt-2"
          >
            {actionLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

// Student Empty States
const EmptyStudentsList = ({ onAddStudent }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" 
        />
      </svg>
    }
    title="No students registered yet"
    description="Start by adding your first student to the system. You can add their basic information, photo, and contact details."
    actionLabel="Add First Student"
    onAction={onAddStudent}
  />
);

// Faculty Empty States
const EmptyFacultyList = ({ onAddFaculty }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
        />
      </svg>
    }
    title="No faculty members registered yet"
    description="Start by adding your first faculty member to the system. You can add their professional information, qualifications, and contact details."
    actionLabel="Add First Faculty"
    onAction={onAddFaculty}
  />
);

const EmptyFacultyDepartment = ({ department, onAddFaculty }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
        />
      </svg>
    }
    title={`No faculty in ${department} department`}
    description={`There are currently no faculty members assigned to the ${department} department. Add faculty members to build your team.`}
    actionLabel="Add Faculty Member"
    onAction={onAddFaculty}
    size="sm"
  />
);

const EmptyFacultySearch = ({ searchTerm, onClearSearch }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
        />
      </svg>
    }
    title="No faculty found"
    description={`No faculty members match your search for "${searchTerm}". Try adjusting your search terms or clear the search to see all faculty.`}
    actionLabel="Clear Search"
    onAction={onClearSearch}
    size="sm"
  />
);

const EmptyFacultyByDesignation = ({ designation, onAddFaculty }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" 
        />
      </svg>
    }
    title={`No ${designation}s found`}
    description={`There are currently no faculty members with the designation of ${designation}. Consider adding faculty members or adjusting your filter.`}
    actionLabel="Add Faculty"
    onAction={onAddFaculty}
    size="sm"
  />
);

// Course Empty States
const EmptyCoursesList = ({ onAddCourse }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
        />
      </svg>
    }
    title="No courses created yet"
    description="Start by creating your first academic course. You can configure the course duration, semesters, and subject structure."
    actionLabel="Add First Course"
    onAction={onAddCourse}
  />
);

// Subject Empty States
const EmptySubjectsList = ({ onAddSubject }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
        />
      </svg>
    }
    title="No subjects created yet"
    description="Start by creating your first academic subject. You can set the subject name, code, credits, and category (core or elective)."
    actionLabel="Add First Subject"
    onAction={onAddSubject}
  />
);

// Academic Structure Empty States
const EmptyAcademicStructure = ({ onAddMapping }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" 
        />
      </svg>
    }
    title="No academic structure configured"
    description="Start by mapping subjects to courses and semesters to create your academic curriculum structure."
    actionLabel="Start Mapping"
    onAction={onAddMapping}
  />
);

// Search Results Empty States
const EmptySearchResults = ({ searchTerm, onClearSearch }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
        />
      </svg>
    }
    title="No results found"
    description={`No results match your search for "${searchTerm}". Try adjusting your search terms or clear the search to see all items.`}
    actionLabel="Clear Search"
    onAction={onClearSearch}
    size="sm"
  />
);

// Table Empty State
const EmptyTableState = ({ message = 'No data to display' }) => (
  <div className="py-12 text-center">
    <EmptyState
      title={message}
      size="sm"
      className="bg-surface-secondary rounded-xl"
    />
  </div>
);

// Generic Empty States for different contexts
const EmptyAssignments = ({ onAddAssignment }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
        />
      </svg>
    }
    title="No assignments yet"
    description="No subjects have been assigned to this semester. Start by adding some subjects."
    actionLabel="Add Assignment"
    onAction={onAddAssignment}
    size="sm"
  />
);

const EmptySelection = ({ message = "Make a selection to continue", description = "" }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
    }
    title={message}
    description={description}
    size="md"
  />
);

const EmptyDashboard = ({ onGetStarted }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
        />
      </svg>
    }
    title="Welcome to NEISSR LMS"
    description="Start by creating courses, adding subjects, configuring your academic structure, and managing faculty."
    actionLabel="Get Started"
    onAction={onGetStarted}
  />
);

// Faculty Analytics Empty States
const EmptyFacultyAnalytics = ({ onAddFaculty }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
        />
      </svg>
    }
    title="No faculty data for analytics"
    description="Add faculty members to the system to view detailed analytics, statistics, and insights about your faculty composition."
    actionLabel="Add Faculty Members"
    onAction={onAddFaculty}
  />
);

// Faculty by Experience Empty State
const EmptyFacultyByExperience = ({ experienceRange, onAddFaculty }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
    }
    title={`No faculty with ${experienceRange} years experience`}
    description={`There are currently no faculty members with ${experienceRange} years of experience. Consider adjusting your filter or adding more faculty.`}
    actionLabel="Add Faculty"
    onAction={onAddFaculty}
    size="sm"
  />
);

// Faculty Reports Empty State
const EmptyFacultyReports = ({ onGenerateReport }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M9 17v-2.586a1 1 0 00-.293-.707L6 11V7a1 1 0 011-1h10a1 1 0 011 1v4l-2.707 2.707A1 1 0 0015 14.414V17m-6 0a1 1 0 001 1h4a1 1 0 001-1m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v8" 
        />
      </svg>
    }
    title="No faculty reports generated"
    description="Generate detailed reports about faculty performance, attendance, and other metrics to track your institution's progress."
    actionLabel="Generate Report"
    onAction={onGenerateReport}
  />
);

export default EmptyState;
export { 
  EmptyState,
  EmptyStudentsList, 
  EmptyCoursesList,
  EmptySubjectsList,
  EmptyAcademicStructure,
  EmptySearchResults, 
  EmptyTableState,
  EmptyAssignments,
  EmptySelection,
  EmptyDashboard,
  EmptyFacultyList,
  EmptyFacultyDepartment,
  EmptyFacultySearch,
  EmptyFacultyByDesignation,
  EmptyFacultyAnalytics,
  EmptyFacultyByExperience,
  EmptyFacultyReports
};